export type PolicyCategory = 'General HR' | 'Legal & Compliance' | 'Operations' | 'Security & IT' | 'Benefits & Payroll';
export type PolicyStatus = 'active' | 'draft' | 'under_review' | 'archived';

export interface CompanyPolicy {
  id: string;
  title: string;
  category: PolicyCategory;
  version: string; // e.g., "v2.4"
  lastUpdated: string; // ISO date
  status: PolicyStatus;
  author: string;
  summary: string; // executive summary (plain text)
  content: string; // full HTML content from rich text editor
  effectiveDate?: string;
  requiresAcknowledgment: boolean;
  appliesTo: string; // 'All Departments' or specific dept
  keyTakeaways?: string[]; // bullet points
}

export interface PolicyAcknowledgment {
  id: string; // `${userId}_${policyId}`
  userId: string;
  userName: string;
  userEmail: string;
  policyId: string;
  policyTitle: string;
  policyVersion: string;
  acknowledgedAt: string; // ISO timestamp
}

export interface PolicyCategoryConfig {
  value: PolicyCategory;
  label: string;
  color: string;
}

export interface PolicyStatusConfig {
  value: PolicyStatus;
  label: string;
  color: string;
}

export interface StaffComplianceData {
  staffId: string;
  staffName: string;
  staffEmail: string;
  department: string;
  avatar?: string;
  policiesAssigned: number;
  policiesSigned: number;
  policiesPending: number;
  compliancePercentage: number;
}

// Default policy categories with colors
export const POLICY_CATEGORIES: PolicyCategoryConfig[] = [
  { value: 'General HR', label: 'General HR', color: '#3b82f6' }, // Blue
  { value: 'Legal & Compliance', label: 'Legal & Compliance', color: '#ef4444' }, // Red
  { value: 'Operations', label: 'Operations', color: '#8b5cf6' }, // Purple
  { value: 'Security & IT', label: 'Security & IT', color: '#6366f1' }, // Indigo
  { value: 'Benefits & Payroll', label: 'Benefits & Payroll', color: '#10b981' }, // Green
];

// Default policy statuses with colors
export const POLICY_STATUSES: PolicyStatusConfig[] = [
  { value: 'active', label: 'Active', color: '#10b981' }, // Green
  { value: 'draft', label: 'Draft', color: '#6b7280' }, // Gray
  { value: 'under_review', label: 'Under Review', color: '#3b82f6' }, // Blue
  { value: 'archived', label: 'Archived', color: '#9ca3af' }, // Light Gray
];

// Helper functions
export function getPolicyCategoryColor(category: PolicyCategory): string {
  const config = POLICY_CATEGORIES.find(c => c.value === category);
  return config?.color || '#6b7280';
}

export function getPolicyStatusColor(status: PolicyStatus): string {
  const config = POLICY_STATUSES.find(s => s.value === status);
  return config?.color || '#6b7280';
}

export function getPolicyStatusLabel(status: PolicyStatus): string {
  const config = POLICY_STATUSES.find(s => s.value === status);
  return config?.label || status;
}
